import { useState } from "react";
import { trackEvent } from "@leafly-com/web-utils";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";

import { setUserMenuPreference } from "redux/action-creators/user";
import { getUserPrefersMedical } from "redux/selectors/user";
import { MENU_TITLES } from "utils/menuTypeUtils";

import { InformationalModal } from "components/botanic/Modal/InformationalModal";
import Radio from "components/botanic/Radio";
import SolidArrowIcon from "components/Icons/arrows/SolidArrowIcon";
import RadioGroup from "components/RadioGroup";

type MenuTypeProps = {
  isDualMenuDispensary: boolean;
  setMenuType: (menuType?: string) => void;
};

const MenuType: React.FC<MenuTypeProps> = ({
  isDualMenuDispensary,
  setMenuType,
}) => {
  const dispatch = useDispatch();

  const {
    query: { menu_type },
  } = useRouter();

  const [opened, setOpened] = useState(false);

  const userPrefersMedical = useSelector(getUserPrefersMedical);

  const medicalPreference = userPrefersMedical ? "Med" : "Rec";
  const menuType = menu_type || medicalPreference;

  const toggleMenuTypesOpened = () => {
    setOpened(!opened);
  };

  const handleMenuTypeChange = (value: string) => {
    setMenuType(value);
    toggleMenuTypesOpened();
    dispatch(setUserMenuPreference(value));

    trackEvent(
      "Dispensary Menu",
      "Click",
      `Toggle to ${value === "Med" ? "Medical" : "Rec"} Menu`
    );
  };

  const modalLabelId = "choose-menu-type-label";

  return (
    <>
      {isDualMenuDispensary && (
        <div
          className="bg-green text-white ml-md px-md py-sm text-sm rounded flex font-bold flex-none"
          onClick={toggleMenuTypesOpened}
          onKeyPress={toggleMenuTypesOpened}
          role="button"
          tabIndex={0}
          data-testid="menu-search-type"
        >
          <div>Menu:</div>
          <div className="text-white ml-md flex">
            <div>{menuType}</div>
            <SolidArrowIcon />
          </div>
        </div>
      )}

      {opened && (
        <InformationalModal
          aria-labelledby={modalLabelId}
          onDismissed={toggleMenuTypesOpened}
        >
          <div id={modalLabelId} className="heading--m">
            Choose your menu
          </div>

          <RadioGroup name={"menuType"} className="mt-xl">
            {[
              { label: "Recreational", value: MENU_TITLES.rec.shortTitle },
              { label: "Medical*", value: MENU_TITLES.med.shortTitle },
            ].map(({ value, label }) => (
              <Radio
                key={value}
                id={value}
                value={value}
                checked={menuType === value}
                label={label}
                ariaLabel={label}
                className="mb-lg"
                onChange={() => {
                  handleMenuTypeChange(value);
                }}
                onClick={() => {
                  handleMenuTypeChange(value);
                }}
                data-testid={`menu-search-type-option-${value}`}
              />
            ))}
          </RadioGroup>

          <div className="mt-xl">
            *You need a valid medical ID to shop from a medical menu. Your ID
            number is required at checkout.
          </div>

          <div className="mt-xl">
            <span className="font-bold">Good news:</span> You can choose to save
            your med ID to your Leafly profile for faster checkout.
          </div>
        </InformationalModal>
      )}
    </>
  );
};

export default MenuType;
